// frontend/src/pages/manufacturing/DXShippingPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, TextField, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Chip,
  Divider, List, ListItem, ListItemText, IconButton, Tooltip,
  Collapse, LinearProgress, InputAdornment
} from '@mui/material';
import {
  ArrowBack, LocalShipping, Print, Delete, Download,
  Visibility, GetApp, KeyboardArrowDown, KeyboardArrowUp,
  Refresh, Warning, TrackChanges, Save, Scale,
  CheckCircle, Label, LabelOff, Undo
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import PackingSlipButton from '../../components/PackingSlipButton';
import { PrinterConfigButton } from '../../components/PrinterConfigDialog';
import { printPdf } from '../../services/printService';
import usePageTitle from '../../hooks/usePageTitle';

// Helper to detect NI postcode
const isNorthernIrelandPostcode = (postcode) => {
  if (!postcode) return false;
  return postcode.toUpperCase().trim().startsWith('BT');
};

// Helper to detect ROI
const isRepublicOfIreland = (postcode, country) => {
  if (country === 'IE' || country === 'Ireland' || country === 'Republic of Ireland') return true;
  
  if (!postcode) return false;
  
  const upper = postcode.toUpperCase().trim().replace(/\s+/g, '');
  
  if (upper === 'DUBLIN' || upper === 'EIRE' || upper === 'IRELAND') return true;
  
  const dublinEircodeRegex = /^D(0[1-9]|1[0-9]|2[0-4])[A-Z0-9]{4}$/;
  if (dublinEircodeRegex.test(upper)) return true;
  
  if (upper.length === 7) {
    const startsLikeEircode = /^[A-Z]\d{2}/.test(upper);
    const endsLikeUKPostcode = /\d[A-Z]{2}$/.test(upper);
    
    if (startsLikeEircode && !endsLikeUKPostcode) {
      return true;
    }
  }
  
  return false;
};

// Helper to extract and clean phone number from text
const extractPhoneNumber = (text) => {
  if (!text) return '7000000000';
  
  const str = String(text);
  
  const ukMobileMatch = str.match(/07\d{9}/);
  if (ukMobileMatch) {
    return ukMobileMatch[0];
  }
  
  const ukLandlineMatch = str.match(/0[12]\d{8,9}/);
  if (ukLandlineMatch) {
    return ukLandlineMatch[0].substring(0, 15);
  }
  
  const intlMatch = str.match(/\+44\d{9,10}/);
  if (intlMatch) {
    return '0' + intlMatch[0].substring(3);
  }
  
  const digitsOnly = str.replace(/\D/g, '');
  const longNumberMatch = digitsOnly.match(/\d{10,11}/);
  if (longNumberMatch) {
    return longNumberMatch[0].substring(0, 15);
  }
  
  if (digitsOnly.length >= 10) {
    return digitsOnly.substring(0, 15);
  }
  
  return '7000000000';
};

// Map delivery_service to DX service code
const mapDeliveryServiceToDXCode = (deliveryService) => {
  switch (deliveryService) {
    case '3DAY':
      return '3D';
    case 'AM':
      return 'AM';
    case 'ON':
    default:
      return 'ON';
  }
};

// Get display label for delivery service
const getDeliveryServiceLabel = (deliveryService) => {
  switch (deliveryService) {
    case '3DAY':
      return '3 Day';
    case 'AM':
      return 'Overnight Pre-Noon';
    case 'ON':
    default:
      return 'Overnight';
  }
};

// Get color for delivery service chip
const getDeliveryServiceColor = (deliveryService) => {
  switch (deliveryService) {
    case 'AM':
      return 'error';
    case '3DAY':
      return 'info';
    case 'ON':
    default:
      return 'success';
  }
};

// Component for expandable consignment row
function ConsignmentRow({ consignment, onPreview, onDownload, onPrint, onComplete, onDelete, onViewTracking, processing, canDeleteShipped }) {
  const [open, setOpen] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'shipped': return 'success';
      case 'label_printed': return 'warning';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  return (
    <>
      <TableRow sx={{ opacity: consignment.status === 'deleted' ? 0.5 : 1 }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!consignment.shipped_lines || consignment.shipped_lines.length === 0}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {consignment.consignment_number || 'No DX Label'}
          </Typography>
          {consignment.label_mode === 'no_dx' && (
            <Chip label="Shipped without DX" size="small" color="info" sx={{ mt: 0.5 }} />
          )}
        </TableCell>
        <TableCell>{consignment.service_description || '-'}</TableCell>
        <TableCell>
          <Chip label={consignment.item_count || 0} size="small" color="primary" />
        </TableCell>
        <TableCell>
          <Chip 
            label={consignment.status} 
            size="small"
            color={getStatusColor(consignment.status)}
          />
        </TableCell>
        <TableCell>
          {consignment.tracking_numbers && consignment.tracking_numbers.length > 0 && (
            <Tooltip title={consignment.tracking_numbers.join(', ')}>
              <Chip 
                label={`${consignment.tracking_numbers.length} tracking #s`}
                size="small"
                variant="outlined"
                onClick={() => onViewTracking(consignment)}
                icon={<TrackChanges />}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          {new Date(consignment.created_at).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </TableCell>
        <TableCell sx={{ minWidth: 280 }}>
          {consignment.status !== 'deleted' && consignment.consignment_number && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {consignment.label_mode !== 'no_dx' && (
                  <>
                <Tooltip title="Print Label">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => onPrint(consignment.consignment_number, consignment.id)}
                    disabled={processing}
                    startIcon={<Print />}
                  >
                    Print
                  </Button>
                </Tooltip>

                <Tooltip title="Preview Label">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onPreview(consignment.consignment_number, consignment.id)}
                    disabled={processing}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Download Label">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onDownload(consignment.consignment_number, consignment.id)}
                    disabled={processing}
                  >
                    <GetApp />
                  </IconButton>
                </Tooltip>
                  </>
                )}

                {(consignment.status !== 'shipped' || canDeleteShipped) && (
                  <Tooltip title={consignment.status === 'shipped'
                    ? 'Delete Consignment (items have been unshipped)'
                    : 'Delete Consignment'}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(consignment)}
                      disabled={processing}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              
              {consignment.status !== 'shipped' && (
                <Tooltip title="Complete Shipment">
                  <Button 
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => onComplete(consignment)}
                    disabled={processing}
                    startIcon={<LocalShipping />}
                  >
                    Ship
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
          {consignment.status !== 'deleted' && !consignment.consignment_number && (
            <Typography variant="body2" color="text.secondary">
              Shipped without DX tracking
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Shipped Items
              </Typography>
              {consignment.shipped_lines && consignment.shipped_lines.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Code</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Labels</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consignment.shipped_lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{line.product_code}</TableCell>
                        <TableCell>{line.product_name}</TableCell>
                        <TableCell align="right">
                          <Chip label={line.quantity} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={line.label_count || 0} 
                            size="small" 
                            color={line.label_count > 0 ? "secondary" : "default"}
                            icon={line.label_count > 0 ? <Label /> : <LabelOff />}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No line items recorded for this consignment
                </Typography>
              )}
              
              {consignment.tracking_numbers && consignment.tracking_numbers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tracking Numbers:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {consignment.tracking_numbers.map((trackingNo, idx) => (
                      <Chip 
                        key={idx} 
                        label={trackingNo} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Tracking Dialog Component
function TrackingDialog({ open, onClose, consignment, sessionKey }) {
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && consignment && sessionKey && consignment.consignment_number) {
      fetchTrackingInfo();
    }
  }, [open, consignment]);

  const fetchTrackingInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await manufacturingAPI.dxShipping.getConsignmentTracking(
        {
          ConsignmentNumber: consignment.consignment_number,
          RoutingStream: consignment.routing_stream || 'F'
        },
        { headers: { 'X-DX-Session-Key': sessionKey } }
      );
      
      if (response.data.Status === 0) {
        setTrackingInfo(response.data);
      } else {
        setError(response.data.StatusMessage || 'Failed to get tracking info');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Tracking Info - {consignment?.consignment_number}
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        
        {trackingInfo && (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>Consignment:</strong> {trackingInfo.ConsignmentNumber}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Item Count:</strong> {trackingInfo.ItemCount}
            </Typography>
            
            {trackingInfo.TrackingNumbers && trackingInfo.TrackingNumbers.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tracking Numbers:
                </Typography>
                <List dense>
                  {trackingInfo.TrackingNumbers.map((t, idx) => (
                    <ListItem key={idx}>
                      <ListItemText 
                        primary={t.TrackingNumber}
                        secondary={`Item ${idx + 1} of ${trackingInfo.ItemCount}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
        
        {!loading && !trackingInfo && !error && (
          <Typography color="text.secondary">
            No tracking information available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={fetchTrackingInfo} disabled={loading}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Delete Confirmation Dialog
function DeleteConfirmDialog({ open, onClose, onConfirm, consignment, processing }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Consignment?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {consignment?.label_mode === 'no_dx'
            ? 'This removes the local shipping record. Nothing was booked with DX for it.'
            : 'This will delete the consignment from DX systems. This action cannot be undone.'}
        </Alert>
        {consignment?.status === 'shipped' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This consignment was already marked as shipped. Only delete it if the
            shipment did not actually go out (e.g. you unshipped it to redo the
            consignment). If DX has already collected it, the cancellation will be rejected.
          </Alert>
        )}
        <Typography>
          Consignment: <strong>{consignment?.consignment_number}</strong>
        </Typography>
        {consignment?.shipped_lines && consignment.shipped_lines.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items in this consignment:
            </Typography>
            <List dense>
              {consignment.shipped_lines.map((line, idx) => (
                <ListItem key={idx}>
                  <ListItemText 
                    primary={line.product_name}
                    secondary={`Qty: ${line.quantity}, Labels: ${line.label_count || 0}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>Cancel</Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          disabled={processing}
          startIcon={processing ? <CircularProgress size={20} /> : <Delete />}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// NI/ROI Additional Fields Component
function InternationalFields({ isNI, isROI, niFields, setNiFields, roiFields, setRoiFields }) {
  if (!isNI && !isROI) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="warning.main">
          <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
          {isNI ? 'Northern Ireland Requirements' : 'Republic of Ireland Requirements'}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {isNI && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              B2C: Goods sent to a private individual for personal use only. Max 31.5kg if multiple items, 100kg for single item.
            </Alert>
            
            <TextField
              label="UK Internal Market Compliant"
              value={niFields.ukimsCompliant}
              onChange={(e) => setNiFields({ ...niFields, ukimsCompliant: e.target.value })}
              helperText="Enter 'CONFIRMED' if goods comply with UK Internal Market Scheme"
              size="small"
            />
            
            <TextField
              label="Delivery EORI Code (Optional for NI)"
              value={niFields.deliveryEORICode}
              onChange={(e) => setNiFields({ ...niFields, deliveryEORICode: e.target.value })}
              helperText="Format: XI followed by 9-12 digits"
              size="small"
            />
            
            <Divider />
            <Typography variant="subtitle2">Declaration Contents</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Commodity Description"
                  value={niFields.commodityContents}
                  onChange={(e) => setNiFields({ ...niFields, commodityContents: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Commodity Code (6 digits)"
                  value={niFields.commodityCode}
                  onChange={(e) => setNiFields({ ...niFields, commodityCode: e.target.value })}
                  size="small"
                  helperText="Optional but recommended"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Value"
                  type="number"
                  value={niFields.commodityValue}
                  onChange={(e) => setNiFields({ ...niFields, commodityValue: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={niFields.commodityCurrency}
                  onChange={(e) => setNiFields({ ...niFields, commodityCurrency: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Origin Country"
                  value={niFields.originCountry}
                  onChange={(e) => setNiFields({ ...niFields, originCountry: e.target.value })}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        )}
        
        {isROI && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Delivery EORI Code (Required)"
              value={roiFields.deliveryEORICode}
              onChange={(e) => setRoiFields({ ...roiFields, deliveryEORICode: e.target.value })}
              helperText="Format: IE followed by 7 characters"
              size="small"
              required
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Invoice Value"
                  type="number"
                  value={roiFields.invoiceValue}
                  onChange={(e) => setRoiFields({ ...roiFields, invoiceValue: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Invoice Currency"
                  value={roiFields.invoiceCurrency}
                  onChange={(e) => setRoiFields({ ...roiFields, invoiceCurrency: e.target.value })}
                  size="small"
                  required
                  placeholder="GBP or EUR"
                />
              </Grid>
            </Grid>
            
            <Divider />
            <Typography variant="subtitle2">Declaration Contents</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Commodity Description"
                  value={roiFields.commodityContents}
                  onChange={(e) => setRoiFields({ ...roiFields, commodityContents: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Commodity Code (8-10 digits)"
                  value={roiFields.commodityCode}
                  onChange={(e) => setRoiFields({ ...roiFields, commodityCode: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Value"
                  type="number"
                  value={roiFields.commodityValue}
                  onChange={(e) => setRoiFields({ ...roiFields, commodityValue: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={roiFields.commodityCurrency}
                  onChange={(e) => setRoiFields({ ...roiFields, commodityCurrency: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Origin Country"
                  value={roiFields.originCountry}
                  onChange={(e) => setRoiFields({ ...roiFields, originCountry: e.target.value })}
                  size="small"
                  required
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function DXShippingPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLines, setSelectedLines] = useState({});
  const [shipQuantities, setShipQuantities] = useState({});
  const [labelCounts, setLabelCounts] = useState({});
  // STOCKITEM special-code entries (free-text rows with no product line)
  const [selectedStockItems, setSelectedStockItems] = useState({});
  const [stockItemLabelCounts, setStockItemLabelCounts] = useState({});
  const [stockItemWeights, setStockItemWeights] = useState({});
  const [processing, setProcessing] = useState(false);
  const [existingConsignments, setExistingConsignments] = useState([]);
  const [sessionKey, setSessionKey] = useState(null);
  
  // Weight management state
  const [lineWeights, setLineWeights] = useState({});
  const [savingWeight, setSavingWeight] = useState({});

  // Heavy goods label (threshold + PDF configured in System Settings)
  const [heavyGoodsSettings, setHeavyGoodsSettings] = useState({ weight_threshold: null, has_label: false });
  const [printingHeavyLabel, setPrintingHeavyLabel] = useState(false);
  
  // Dialogs
  const [labelPreviewDialog, setLabelPreviewDialog] = useState(false);
  const [previewLabelData, setPreviewLabelData] = useState(null);
  const [previewConsignmentNumber, setPreviewConsignmentNumber] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [consignmentToDelete, setConsignmentToDelete] = useState(null);
  const [trackingDialog, setTrackingDialog] = useState(false);
  const [trackingConsignment, setTrackingConsignment] = useState(null);

  // Unship dialog
  const [unshipDialogOpen, setUnshipDialogOpen] = useState(false);
  const [unshipping, setUnshipping] = useState(false);

  usePageTitle(order ? `DX Shipping: ${order.order_number}` : null);
  
  // NI/ROI detection and fields
  const [isNI, setIsNI] = useState(false);
  const [isROI, setIsROI] = useState(false);
  const [niFields, setNiFields] = useState({
    deliveryType: '1',
    ukimsCompliant: 'CONFIRMED',
    deliveryEORICode: '',
    commodityContents: 'General Goods',
    commodityCode: '',
    commodityValue: '',
    commodityCurrency: 'GBP',
    originCountry: 'GB',
    commodityWeight: ''
  });
  const [roiFields, setRoiFields] = useState({
    deliveryEORICode: '',
    invoiceValue: '',
    invoiceCurrency: 'GBP',
    commodityContents: 'General Goods',
    commodityCode: '',
    commodityValue: '',
    commodityCurrency: 'GBP',
    originCountry: 'GB',
    commodityWeight: ''
  });

  useEffect(() => {
    loadOrderDetails();
    loadExistingConsignments();
    initializeSession();
    loadHeavyGoodsSettings();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      setIsNI(isNorthernIrelandPostcode(order.delivery_postcode));
      setIsROI(isRepublicOfIreland(order.delivery_postcode, order.delivery_country));
      
      const initialWeights = {};
      order.lines.forEach(line => {
        initialWeights[line.id] = parseFloat(line.product_weight) || 0;
      });
      setLineWeights(initialWeights);
    }
  }, [order]);

  // Auto-select STOCKITEM entries that are not yet on a consignment
  useEffect(() => {
    if (!order) return;
    const selected = {};
    const labels = {};
    (order.special_codes || [])
      .filter(sc => sc.code_type_display === 'STOCKITEM')
      .forEach(sc => {
        const consigned = existingConsignments.some(c =>
          c.status !== 'deleted' &&
          (c.shipped_lines || []).some(l => !l.line_id && l.order_code_id === sc.id)
        );
        if (!consigned) {
          selected[sc.id] = true;
          labels[sc.id] = 1;
        }
      });
    setSelectedStockItems(selected);
    setStockItemLabelCounts(prev => ({ ...labels, ...prev }));
  }, [order, existingConsignments]);

  const initializeSession = async () => {
    try {
      const response = await manufacturingAPI.dxShipping.getSessionKey({
        DXAccountNumber: process.env.REACT_APP_DX_ACCOUNT,
        OrigServiceCentre: process.env.REACT_APP_DX_SERVICE_CENTRE,
        Password: process.env.REACT_APP_DX_PASSWORD
      });
      
      if (response.data.SessionKey) {
        setSessionKey(response.data.SessionKey);
      }
    } catch (error) {
      console.error('Failed to get session key:', error);
    }
  };

  // Calculate quantities already in pending (non-shipped, non-deleted) consignments
  const getPendingQuantities = () => {
    const pending = {};
    existingConsignments
      .filter(c => c.status !== 'shipped' && c.status !== 'deleted')
      .forEach(consignment => {
        (consignment.shipped_lines || []).forEach(line => {
          if (!line.line_id) return; // STOCKITEM entries are tracked separately
          const lineId = line.line_id;
          pending[lineId] = (pending[lineId] || 0) + line.quantity;
        });
      });
    return pending;
  };

  // STOCKITEM special-code entries on this order
  const getStockItems = () =>
    (order?.special_codes || []).filter(sc => sc.code_type_display === 'STOCKITEM');

  // 'shipped' | 'pending' | 'available' for a STOCKITEM entry, based on consignments
  const getStockItemState = (codeId) => {
    for (const c of existingConsignments) {
      if (c.status === 'deleted') continue;
      for (const l of (c.shipped_lines || [])) {
        if (!l.line_id && l.order_code_id === codeId) {
          return c.status === 'shipped' ? 'shipped' : 'pending';
        }
      }
    }
    return 'available';
  };

  // A shipped consignment becomes deletable once its items have been unshipped:
  // the total quantity recorded across shipped consignments exceeds what the
  // order lines still show as shipped. Consignments on genuinely shipped
  // orders never qualify. STOCKITEM-only consignments (no product lines, so no
  // unship path) are always deletable.
  const isShippedConsignmentDeletable = (consignment) => {
    if (consignment.status !== 'shipped') return false;
    const lineEntries = (consignment.shipped_lines || []).filter(l => l.line_id);
    if (lineEntries.length === 0) {
      return (consignment.shipped_lines || []).length > 0;
    }
    return lineEntries.some(entry => {
      const line = order?.lines?.find(l => l.id === entry.line_id);
      if (!line) return false;
      const coverage = existingConsignments
        .filter(c => c.status === 'shipped')
        .reduce((sum, c) => sum + (c.shipped_lines || [])
          .filter(l => l.line_id === entry.line_id)
          .reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0), 0);
      return coverage > (parseFloat(line.quantity_shipped) || 0);
    });
  };

  const handleUnshipOrder = async () => {
    setUnshipping(true);
    try {
      const response = await manufacturingAPI.customerOrders.unshipOrder(orderId);
      toast.success(response.data.message || 'Order unshipped');
      (response.data.consignments_deleted || []).forEach(number =>
        toast.info(`Consignment ${number} cancelled at DX and deleted - create a new one to re-ship`)
      );
      (response.data.consignment_warnings || []).forEach(warning => toast.warn(warning));
      setUnshipDialogOpen(false);
      await loadOrderDetails();
      await loadExistingConsignments();
    } catch (error) {
      console.error('Failed to unship order:', error);
      toast.error(error.response?.data?.error || 'Failed to unship order');
    } finally {
      setUnshipping(false);
    }
  };

  // Handle weight change for a line
  const handleWeightChange = (lineId, value) => {
    const weight = parseFloat(value) || 0;
    setLineWeights(prev => ({
      ...prev,
      [lineId]: weight
    }));
  };

  // Save weight to database
  const handleSaveWeight = async (line) => {
    const weight = lineWeights[line.id];
    
    if (weight <= 0) {
      toast.error('Please enter a valid weight greater than 0');
      return;
    }
    
    setSavingWeight(prev => ({ ...prev, [line.id]: true }));
    
    try {
      const productId = line.product;
      await manufacturingAPI.items.updateWeight(productId, weight);
      
      toast.success(`Weight saved for ${line.product_display}: ${weight} kg`);
      
      setOrder(prev => ({
        ...prev,
        lines: prev.lines.map(l => 
          l.id === line.id 
            ? { ...l, product_weight: weight }
            : l
        )
      }));
      
    } catch (error) {
      console.error('Failed to save weight:', error);
      toast.error('Failed to save weight: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingWeight(prev => ({ ...prev, [line.id]: false }));
    }
  };

  // Get effective weight for a line
  const getEffectiveWeight = (line) => {
    const enteredWeight = lineWeights[line.id];
    if (enteredWeight && enteredWeight > 0) {
      return enteredWeight;
    }
    return parseFloat(line.product_weight) || 1;
  };

  const loadHeavyGoodsSettings = async () => {
    try {
      const response = await manufacturingAPI.heavyGoods.getSettings();
      setHeavyGoodsSettings(response.data);
    } catch (error) {
      console.error('Failed to load heavy goods settings:', error);
    }
  };

  // A unit is "heavy" when a threshold is set, a label PDF exists, and the
  // per-unit weight is at or above the threshold
  const isHeavyWeight = (unitWeightKg) => {
    const threshold = parseFloat(heavyGoodsSettings.weight_threshold);
    return heavyGoodsSettings.has_label && !isNaN(threshold) && unitWeightKg >= threshold;
  };

  const handlePrintHeavyGoodsLabel = async () => {
    setPrintingHeavyLabel(true);
    try {
      const response = await manufacturingAPI.heavyGoods.getLabel();
      await printPdf(response.data.label, 'label');
      toast.success('Heavy goods label sent to printer');
    } catch (error) {
      console.error('Failed to print heavy goods label:', error);
      toast.error(error.response?.data?.error || 'Failed to print heavy goods label');
    } finally {
      setPrintingHeavyLabel(false);
    }
  };

  const loadOrderDetails = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.get(orderId);
      const orderData = response.data;
      setOrder(orderData);
      
      const initialQuantities = {};
      const initialSelected = {};
      const initialLabelCounts = {};
      
      orderData.lines.forEach(line => {
        const allocated = line.quantity_allocated || 0;
        // quantity_allocated already represents only the current remaining allocation
        // (shipping decrements allocations), so allocated IS the available amount
        const availableToShip = allocated;

        if (availableToShip > 0) {
          initialQuantities[line.id] = availableToShip;
          initialSelected[line.id] = true;
          initialLabelCounts[line.id] = 1;
        }
      });
      
      setShipQuantities(initialQuantities);
      setSelectedLines(initialSelected);
      setLabelCounts(initialLabelCounts);
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingConsignments = async () => {
    try {
      const response = await manufacturingAPI.dxShipping.getConsignments(orderId);
      setExistingConsignments(response.data);
    } catch (error) {
      console.error('Failed to load consignments:', error);
    }
  };

  const handleSelectLine = (lineId, checked) => {
    setSelectedLines(prev => ({
      ...prev,
      [lineId]: checked
    }));
  };

  const handleQuantityChange = (lineId, value, maxAvailable) => {
    const qty = Math.min(Math.max(0, parseInt(value) || 0), maxAvailable);
    setShipQuantities(prev => ({
      ...prev,
      [lineId]: qty
    }));
  };

  const handleLabelCountChange = (lineId, value) => {
    const count = Math.max(0, parseInt(value) || 0);
    setLabelCounts(prev => ({
      ...prev,
      [lineId]: count
    }));
  };

  const handleSelectAll = () => {
    const newSelected = {};
    const newQuantities = {};
    const newLabelCounts = {};
    const pendingQuantities = getPendingQuantities();
    
    order.lines.forEach(line => {
      const allocated = line.quantity_allocated || 0;
      const pending = pendingQuantities[line.id] || 0;
      const availableToShip = Math.max(0, allocated - pending);

      if (availableToShip > 0) {
        newSelected[line.id] = true;
        newQuantities[line.id] = availableToShip;
        newLabelCounts[line.id] = labelCounts[line.id] || 1;
      }
    });
    
    setSelectedLines(newSelected);
    setShipQuantities(newQuantities);
    setLabelCounts(newLabelCounts);

    const newStockSelected = {};
    getStockItems().forEach(sc => {
      if (getStockItemState(sc.id) === 'available') {
        newStockSelected[sc.id] = true;
      }
    });
    setSelectedStockItems(newStockSelected);
  };

  const handleDeselectAll = () => {
    setSelectedLines({});
    setSelectedStockItems({});
  };

  const getSessionKeyForOperation = async () => {
    if (sessionKey) return sessionKey;
    
    const response = await manufacturingAPI.dxShipping.getSessionKey({
      DXAccountNumber: process.env.REACT_APP_DX_ACCOUNT,
      OrigServiceCentre: process.env.REACT_APP_DX_SERVICE_CENTRE,
      Password: process.env.REACT_APP_DX_PASSWORD
    });
    
    if (response.data.SessionKey) {
      setSessionKey(response.data.SessionKey);
      return response.data.SessionKey;
    }
    
    throw new Error('Failed to get session key');
  };

  // Get the DX service code from order's delivery_service
  const getServiceCode = () => {
    return mapDeliveryServiceToDXCode(order?.delivery_service);
  };

  // Build consignment request with all labels across all lines
  const buildConsignmentRequest = (shippedLines, totalLabels, totalWeight) => {
    // DX validates the manifest DATE. Sending UTC "now" between midnight and
    // 1am BST yields yesterday's date and DX rejects it ("Invalid
    // ManifestDate"). Anchor to midday local time so the date part is always
    // today's local date whichever timezone DX interprets it in.
    const now = new Date();
    const manifestDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0).toISOString();
    const serviceCode = getServiceCode();

    // Build contents array - one entry per line with ContentQuantity = label count
    // DX generates one tracking number/label per ContentQuantity unit
    // ContentTotalWeight is the total weight for the entire content record
    const contents = [];

    shippedLines.forEach(line => {
      const labelCount = line.label_count || 0;
      if (labelCount > 0) {
        contents.push({
          ContentDescriptionID: 1,
          ContentDescription: "CartonKG",
          ContentQuantity: labelCount,
          ContentTotalWeight: Math.max(1, Math.ceil(line.total_weight)),
          ContentDimension1: 0,
          ContentDimension2: 0,
          ContentDimension3: 0
        });
      }
    });

    // If no labels at all, add one minimal content entry (DX requires at least one)
    if (contents.length === 0) {
      contents.push({
        ContentDescriptionID: 1,
        ContentDescription: "CartonKG",
        ContentQuantity: 1,
        ContentTotalWeight: Math.max(1, Math.ceil(totalWeight)),
        ContentDimension1: 0,
        ContentDimension2: 0,
        ContentDimension3: 0
      });
    }
    
    const request = {
      DXAccountNumber: process.env.REACT_APP_DX_ACCOUNT,
      OrigServiceCentre: process.env.REACT_APP_DX_SERVICE_CENTRE,
      ServiceCode: serviceCode,
      ManifestDate: manifestDate,
      ConsignmentReference1: order.order_number.substring(0, 10),
      ConsignmentReference2: '',
      DeliveryName: (order.delivery_name || order.customer_name || '').substring(0, 25),
      DeliveryAddress1: (order.delivery_name || order.customer_name || '').substring(0, 25),
      DeliveryAddress2: (order.delivery_line_1 || '').substring(0, 30),
      DeliveryAddress3: (order.delivery_line_2 || '').substring(0, 30),
      DeliveryAddress4: [order.delivery_city, order.delivery_county].filter(Boolean).join(', ').substring(0, 30),
      DeliveryAddress5: '',
      DeliveryPostcode: order.delivery_postcode,
      DeliveryPhoneNumber: extractPhoneNumber(order.delivery_phone || ''),
      DeliveryContact: (order.delivery_contact_name || order.customer_name || '').substring(0, 25),
      DeliveryEmail: '',
      BookInFlag: false,
      InsuranceValue: 0,
      PrivateAddress: false,
      SpecialInstruction1: (order.dx_note_1 || '').substring(0, 25),
      SpecialInstruction2: (order.dx_note_2 || '').substring(0, 25),
      SpecialInstruction3: (order.dx_note_3 || '').substring(0, 25),
      Contents: contents
    };
    
    // Add NI-specific fields
    if (isNI) {
      request.DeliveryCountry = 'XI';
      request.DeliveryType = parseInt(niFields.deliveryType);
      
      if (niFields.deliveryType === '2') {
        request.UKInternalMarketCompliant = niFields.ukimsCompliant;
      }
      
      if (niFields.deliveryEORICode) {
        request.DeliveryEORICode = niFields.deliveryEORICode;
      }
      
      const totalQty = shippedLines.reduce((sum, l) => sum + l.quantity, 0);
      
      request.DeclarationContents = [{
        CommodityQuantity: totalQty,
        CommodityContents: niFields.commodityContents.substring(0, 200),
        CommodityCode: niFields.commodityCode || '',
        OriginatingCountry: niFields.deliveryType === '2' ? niFields.originCountry : '',
        WeightofCommodity: String(totalWeight),
        ValueofCommodity: niFields.commodityValue || '0',
        ValueofCommodityCurrency: niFields.commodityCurrency
      }];
    }
    
    // Add ROI-specific fields
    if (isROI) {
      request.DeliveryCountry = 'IE';
      request.DeliveryEORICode = roiFields.deliveryEORICode;
      request.InvoiceValue = roiFields.invoiceValue;
      request.InvoiceValueCurrency = roiFields.invoiceCurrency;
      
      const totalQty = shippedLines.reduce((sum, l) => sum + l.quantity, 0);
      
      request.DeclarationContents = [{
        CommodityQuantity: totalQty,
        CommodityContents: roiFields.commodityContents.substring(0, 200),
        CommodityCode: roiFields.commodityCode,
        OriginatingCountry: roiFields.originCountry,
        WeightofCommodity: String(totalWeight),
        ValueofCommodity: roiFields.commodityValue || '0',
        ValueofCommodityCurrency: roiFields.commodityCurrency
      }];
    }
    
    return { request, manifestDate };
  };

  // Create consignment with DX labels
  const handleCreateConsignment = async () => {
    const pendingQuantities = getPendingQuantities();
    const selectedLinesList = order.lines.filter(line => selectedLines[line.id]);
    const selectedStockList = getStockItems().filter(sc =>
      selectedStockItems[sc.id] && getStockItemState(sc.id) === 'available'
    );

    if (selectedLinesList.length === 0 && selectedStockList.length === 0) {
      toast.error('Please select at least one line to ship');
      return;
    }
    
    // Validate NI/ROI fields
    if (isNI && !niFields.commodityContents) {
      toast.error('Commodity description is required for Northern Ireland deliveries');
      return;
    }
    
    if (isROI) {
      if (!roiFields.deliveryEORICode) {
        toast.error('EORI Code is required for Republic of Ireland deliveries');
        return;
      }
      if (!roiFields.invoiceValue || !roiFields.commodityCode) {
        toast.error('Invoice value and commodity code are required for ROI deliveries');
        return;
      }
    }
    
    // Build shipped lines data
    let totalWeight = 0;
    let totalLabels = 0;
    let totalItems = 0;
    const shippedLines = [];
    
    selectedLinesList.forEach(line => {
      const allocated = line.quantity_allocated || 0;
      const pending = pendingQuantities[line.id] || 0;
      const availableToShip = Math.max(0, allocated - pending);
      const quantity = Math.min(shipQuantities[line.id] || 0, availableToShip);
      const lineLabelCount = labelCounts[line.id] || 0;

      if (quantity > 0) {
        const itemWeight = getEffectiveWeight(line);
        const lineWeight = itemWeight * quantity;
        totalWeight += lineWeight;
        totalLabels += lineLabelCount;
        totalItems += quantity;
        
        shippedLines.push({
          line_id: line.id,
          product_code: line.product_display,
          product_name: line.product_name,
          quantity: quantity,
          label_count: lineLabelCount,
          weight_per_item: itemWeight,
          total_weight: lineWeight
        });
      }
    });

    // STOCKITEM entries ship as a single unit each (free text, no product line)
    selectedStockList.forEach(sc => {
      const weight = stockItemWeights[sc.id] > 0 ? stockItemWeights[sc.id] : 1;
      const labelCount = stockItemLabelCounts[sc.id] !== undefined ? stockItemLabelCounts[sc.id] : 1;

      totalWeight += weight;
      totalLabels += labelCount;
      totalItems += 1;

      shippedLines.push({
        line_id: null,
        order_code_id: sc.id,
        product_code: 'STOCKITEM',
        product_name: sc.value,
        quantity: 1,
        label_count: labelCount,
        weight_per_item: weight,
        total_weight: weight
      });
    });

    if (totalItems === 0) {
      toast.error('Please enter quantities to ship');
      return;
    }
    
    if (totalLabels === 0) {
      toast.error('Please specify at least 1 label, or use "Ship Without DX" for items without labels');
      return;
    }
    
    setProcessing(true);
    
    try {
      const currentSessionKey = await getSessionKeyForOperation();
      
      const { request, manifestDate } = buildConsignmentRequest(shippedLines, totalLabels, totalWeight);
      
      const consignmentResponse = await manufacturingAPI.dxShipping.addConsignment(
        request,
        { headers: { 'X-DX-Session-Key': currentSessionKey } }
      );
      
      const consignment = consignmentResponse.data;
      
      if (consignment.Status === 0) {
        await manufacturingAPI.dxShipping.saveConsignment({
          order_id: orderId,
          consignment_data: consignment,
          manifest_date: manifestDate,
          shipped_lines: shippedLines,
          label_mode: 'combined'
        });
        
        await loadExistingConsignments();
        
        // Reset selection
        setSelectedLines({});
        setShipQuantities({});
        setLabelCounts({});
        setSelectedStockItems({});
        
        // Re-initialize with remaining available
        const updatedPending = getPendingQuantities();
        order.lines.forEach(line => {
          const allocated = line.quantity_allocated || 0;
          const pending = updatedPending[line.id] || 0;
          const newAvailable = Math.max(0, allocated - pending);
          
          if (newAvailable > 0) {
            setShipQuantities(prev => ({ ...prev, [line.id]: newAvailable }));
            setLabelCounts(prev => ({ ...prev, [line.id]: 1 }));
          }
        });
        
        toast.success(
          `Consignment created: ${consignment.ConsignmentNumber} with ${totalLabels} label${totalLabels !== 1 ? 's' : ''} for ${totalItems} items`
        );
        
      } else {
        toast.error(`DX Error: ${consignment.StatusMessage}`);
      }
      
    } catch (error) {
      console.error('Failed to create consignment:', error);
      toast.error(error.response?.data?.error || 'Failed to create DX consignment');
    } finally {
      setProcessing(false);
    }
  };

  // Ship without DX - just mark as shipped
  const handleShipWithoutDX = async () => {
    const pendingQuantities = getPendingQuantities();
    const selectedLinesList = order.lines.filter(line => selectedLines[line.id]);
    const selectedStockList = getStockItems().filter(sc =>
      selectedStockItems[sc.id] && getStockItemState(sc.id) === 'available'
    );

    if (selectedLinesList.length === 0 && selectedStockList.length === 0) {
      toast.error('Please select at least one line to ship');
      return;
    }

    const shippedLines = [];
    let totalItems = 0;

    selectedLinesList.forEach(line => {
      const allocated = line.quantity_allocated || 0;
      const pending = pendingQuantities[line.id] || 0;
      const availableToShip = Math.max(0, allocated - pending);
      const quantity = Math.min(shipQuantities[line.id] || 0, availableToShip);

      if (quantity > 0) {
        totalItems += quantity;
        shippedLines.push({
          line_id: line.id,
          product_code: line.product_display,
          product_name: line.product_name,
          quantity: quantity,
          label_count: 0,
          weight_per_item: getEffectiveWeight(line),
          total_weight: getEffectiveWeight(line) * quantity
        });
      }
    });

    selectedStockList.forEach(sc => {
      const weight = stockItemWeights[sc.id] > 0 ? stockItemWeights[sc.id] : 1;
      totalItems += 1;
      shippedLines.push({
        line_id: null,
        order_code_id: sc.id,
        product_code: 'STOCKITEM',
        product_name: sc.value,
        quantity: 1,
        label_count: 0,
        weight_per_item: weight,
        total_weight: weight
      });
    });

    if (totalItems === 0) {
      toast.error('Please enter quantities to ship');
      return;
    }
    
    setProcessing(true);
    
    try {
      // Ship each product line (STOCKITEM entries have no line and no stock effect)
      for (const shippedLine of shippedLines) {
        if (!shippedLine.line_id) continue;
        await manufacturingAPI.customerOrders.shipQuantity(orderId, {
          line_id: shippedLine.line_id,
          quantity: shippedLine.quantity
        });
      }
      
      // Save a record without DX consignment
      await manufacturingAPI.dxShipping.saveConsignment({
        order_id: orderId,
        consignment_data: null,
        manifest_date: new Date().toISOString(),
        shipped_lines: shippedLines,
        label_mode: 'no_dx',
        status: 'shipped'
      });
      
      await loadExistingConsignments();
      await loadOrderDetails();
      
      toast.success(`Shipped ${totalItems} items without DX tracking`);
      
    } catch (error) {
      console.error('Failed to ship:', error);
      toast.error(error.response?.data?.error || 'Failed to ship items');
    } finally {
      setProcessing(false);
    }
  };

  const handleGetLabel = async (consignmentNumber, consignmentId = null) => {
    setProcessing(true);
    
    try {
      const currentSessionKey = await getSessionKeyForOperation();

      if (!consignmentNumber) {
        toast.error('No consignment number available');
        setProcessing(false);
        return;
      }

      const labelResponse = await manufacturingAPI.dxShipping.getLabels(
        {
          ConsignmentNumber: consignmentNumber,
          LabelReturnType: 0,
          PrintSelection: 2,
          RoutingStream: 'F',
          PDFLabelConfig: {
            labelSetup: 1,
            startingPosition: 1,
          },
        },
        { headers: { 'X-DX-Session-Key': currentSessionKey } }
      );

      if (labelResponse.data.Status === 0 && labelResponse.data.label) {
        const byteCharacters = atob(labelResponse.data.label);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `DX_Label_${consignmentNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Label downloaded successfully');
        
        if (consignmentId) {
          const consignmentRecord = existingConsignments.find(c => c.id === consignmentId);
          if (consignmentRecord && consignmentRecord.status === 'created') {
            await manufacturingAPI.dxShipping.updateConsignmentStatus(consignmentRecord.id, {
              status: 'label_printed'
            });
            await loadExistingConsignments();
          }
        }
      } else {
        throw new Error(labelResponse.data.StatusMessage || 'Failed to get label');
      }
    } catch (err) {
      console.error('Failed to get label:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to get label');
    } finally {
      setProcessing(false);
    }
  };

  const handlePreviewLabel = async (consignmentNumber, consignmentId = null) => {
    setProcessing(true);
    
    try {
      const currentSessionKey = await getSessionKeyForOperation();

      const labelResponse = await manufacturingAPI.dxShipping.getLabels(
        {
          ConsignmentNumber: consignmentNumber,
          LabelReturnType: 0,
          PrintSelection: 2,
          RoutingStream: 'F',
          PDFLabelConfig: {
            labelSetup: 1,
            startingPosition: 1,
          },
        },
        { headers: { 'X-DX-Session-Key': currentSessionKey } }
      );

      if (labelResponse.data.Status === 0 && labelResponse.data.label) {
        setPreviewLabelData(labelResponse.data.label);
        setPreviewConsignmentNumber(consignmentNumber);
        setLabelPreviewDialog(true);
        
        if (consignmentId) {
          const consignmentRecord = existingConsignments.find(c => c.id === consignmentId);
          if (consignmentRecord && consignmentRecord.status === 'created') {
            await manufacturingAPI.dxShipping.updateConsignmentStatus(consignmentRecord.id, {
              status: 'label_printed'
            });
            await loadExistingConsignments();
          }
        }
      } else {
        throw new Error(labelResponse.data.StatusMessage || 'Failed to get label');
      }
    } catch (err) {
      console.error('Failed to preview label:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to preview label');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrintLabel = async (consignmentNumber, consignmentId = null) => {
    setProcessing(true);
    
    try {
      const currentSessionKey = await getSessionKeyForOperation();

      const labelResponse = await manufacturingAPI.dxShipping.getLabels(
        {
          ConsignmentNumber: consignmentNumber,
          LabelReturnType: 0,
          PrintSelection: 2,
          RoutingStream: 'F',
          PDFLabelConfig: {
            labelSetup: 1,
            startingPosition: 1,
          },
        },
        { headers: { 'X-DX-Session-Key': currentSessionKey } }
      );

      if (labelResponse.data.Status === 0 && labelResponse.data.label) {
        // Use the print service for smart printer routing
        const result = await printPdf(labelResponse.data.label, 'label');
        
        if (result.method === 'qz') {
          toast.success(`Label sent to ${result.printer}`);
        }
        // Browser print shows dialog, no extra toast needed
        
        // Update status if needed
        if (consignmentId) {
          const consignmentRecord = existingConsignments.find(c => c.id === consignmentId);
          if (consignmentRecord && consignmentRecord.status === 'created') {
            await manufacturingAPI.dxShipping.updateConsignmentStatus(consignmentRecord.id, {
              status: 'label_printed'
            });
            await loadExistingConsignments();
          }
        }
      } else {
        throw new Error(labelResponse.data.StatusMessage || 'Failed to get label');
      }
    } catch (err) {
      console.error('Failed to print label:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to print label');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadFromPreview = () => {
    if (!previewLabelData) return;
    
    const byteCharacters = atob(previewLabelData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DX_Label_${previewConsignmentNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Label downloaded');
  };

  const handlePrintFromPreview = () => {
    if (!previewLabelData) return;
    
    const byteCharacters = atob(previewLabelData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const handleDeleteConsignment = (consignment) => {
    setConsignmentToDelete(consignment);
    setDeleteConfirmDialog(true);
  };

  const confirmDeleteConsignment = async () => {
    if (!consignmentToDelete) return;
    
    setProcessing(true);
    
    try {
      // No-DX records only exist locally - there is nothing to cancel at DX
      if (consignmentToDelete.consignment_number && consignmentToDelete.label_mode !== 'no_dx') {
        const currentSessionKey = await getSessionKeyForOperation();
        const deleteResponse = await manufacturingAPI.dxShipping.deleteConsignment(
          {
            ConsignmentNumber: consignmentToDelete.consignment_number,
            RoutingStream: consignmentToDelete.routing_stream || 'F'
          },
          { headers: { 'X-DX-Session-Key': currentSessionKey } }
        );

        if (deleteResponse.data.Status !== 0) {
          toast.error(deleteResponse.data.StatusMessage || 'Failed to delete consignment from DX');
          setProcessing(false);
          return;
        }
      }
      
      await manufacturingAPI.dxShipping.updateConsignmentStatus(consignmentToDelete.id, {
        status: 'deleted'
      });
      
      toast.success('Consignment deleted successfully');
      
      await loadExistingConsignments();
      await loadOrderDetails();
    } catch (error) {
      console.error('Failed to delete consignment:', error);
      toast.error(error.response?.data?.error || 'Failed to delete consignment');
    } finally {
      setProcessing(false);
      setDeleteConfirmDialog(false);
      setConsignmentToDelete(null);
    }
  };

  const handleViewTracking = (consignment) => {
    setTrackingConsignment(consignment);
    setTrackingDialog(true);
  };

  const handleCompleteConsignment = async (consignment) => {
    setProcessing(true);
    
    try {
      const response = await manufacturingAPI.dxShipping.completeShipment(consignment.id);
      
      if (response.data.success) {
        toast.success(`Shipment ${consignment.consignment_number} completed successfully`);
        
        await loadExistingConsignments();
        await loadOrderDetails();
      } else {
        const errors = response.data.results?.errors || [];
        if (errors.length > 0) {
          errors.forEach(err => toast.error(`${err.product}: ${err.error}`));
        } else {
          toast.error('Failed to complete shipment');
        }
      }
    } catch (error) {
      console.error('Failed to complete shipment:', error);
      toast.error(error.response?.data?.error || 'Failed to complete shipment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Order not found</Alert>
        <Button onClick={() => navigate('/manufacturing/customer-orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const pendingQuantities = getPendingQuantities();
  const selectedStockItemsList = getStockItems().filter(sc =>
    selectedStockItems[sc.id] && getStockItemState(sc.id) === 'available'
  );
  const selectedLinesCount =
    Object.values(selectedLines).filter(Boolean).length + selectedStockItemsList.length;

  // Calculate totals for selected lines
  let totalItemsToShip = 0;
  let totalLabelsToCreate = 0;
  let itemsWithoutLabels = 0;
  let totalShipmentWeight = 0;
  let linesUsingDefaultWeight = 0;

  order.lines.forEach(line => {
    if (selectedLines[line.id]) {
      const allocated = line.quantity_allocated || 0;
      const pending = pendingQuantities[line.id] || 0;
      const available = Math.max(0, allocated - pending);
      const qty = Math.min(shipQuantities[line.id] || 0, available);
      const labels = labelCounts[line.id] || 0;
      const effectiveWeight = getEffectiveWeight(line);
      const savedWeight = parseFloat(line.product_weight) || 0;
      const enteredWeight = lineWeights[line.id];
      const hasRealWeight = savedWeight > 0 || (enteredWeight && enteredWeight > 0);

      totalItemsToShip += qty;
      totalLabelsToCreate += labels;
      totalShipmentWeight += qty * effectiveWeight;
      if (qty > 0 && !hasRealWeight) {
        linesUsingDefaultWeight++;
      }
      if (qty > 0 && labels === 0) {
        itemsWithoutLabels += qty;
      }
    }
  });

  // Selected STOCKITEM entries ship as one unit each
  selectedStockItemsList.forEach(sc => {
    const weight = stockItemWeights[sc.id] > 0 ? stockItemWeights[sc.id] : 1;
    const labels = stockItemLabelCounts[sc.id] !== undefined ? stockItemLabelCounts[sc.id] : 1;

    totalItemsToShip += 1;
    totalLabelsToCreate += labels;
    totalShipmentWeight += weight;
    if (!(stockItemWeights[sc.id] > 0)) {
      linesUsingDefaultWeight++;
    }
    if (labels === 0) {
      itemsWithoutLabels += 1;
    }
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Typography variant="h4" gutterBottom>
          DX Freight Shipping
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Order: <strong>{order.order_number}</strong> - {order.customer_name}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <PackingSlipButton order={order} variant="outlined" />
          <PrinterConfigButton />
          {order.lines?.some(l => (parseFloat(l.quantity_shipped) || 0) > 0) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Undo />}
              onClick={() => setUnshipDialogOpen(true)}
              disabled={processing || unshipping}
            >
              Unship Order
            </Button>
          )}
        </Box>
        
        {isNI && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Northern Ireland Delivery</strong> - Additional Windsor Framework requirements apply.
          </Alert>
        )}
        {isROI && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Republic of Ireland Delivery</strong> - International customs declaration required.
          </Alert>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Existing Consignments */}
        {existingConsignments.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Shipment History
                  </Typography>
                  <Button 
                    size="small" 
                    startIcon={<Refresh />}
                    onClick={loadExistingConsignments}
                  >
                    Refresh
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Consignment No.</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Labels</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tracking</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existingConsignments.map((cons) => (
                        <ConsignmentRow
                          key={cons.id}
                          consignment={cons}
                          onPreview={handlePreviewLabel}
                          onDownload={handleGetLabel}
                          onPrint={handlePrintLabel}
                          onComplete={handleCompleteConsignment}
                          onDelete={handleDeleteConsignment}
                          onViewTracking={handleViewTracking}
                          processing={processing}
                          canDeleteShipped={isShippedConsignmentDeletable(cons)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* NI/ROI Additional Fields */}
        <Grid item xs={12}>
          <InternationalFields 
            isNI={isNI}
            isROI={isROI}
            niFields={niFields}
            setNiFields={setNiFields}
            roiFields={roiFields}
            setRoiFields={setRoiFields}
          />
        </Grid>

        {/* Item Selection */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Select Items to Ship</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </Button>
                </Box>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>How it works:</strong>
                <br />
                1. Select items and set quantities to ship
                <br />
                2. Set number of labels for each line (0 = item ships but gets no label, useful for small items sharing a box)
                <br />
                3. Click "Create Consignment" for ONE consignment with all your labels, or "Ship Without DX" to just mark as shipped
              </Alert>
              
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Ship</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="center">Qty to Ship</TableCell>
                      <TableCell align="center">Weight (kg)</TableCell>
                      <TableCell align="center">Labels</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.lines.map(line => {
                      const allocated = line.quantity_allocated || 0;
                      const shipped = line.quantity_shipped || 0;
                      const pending = pendingQuantities[line.id] || 0;
                      const availableToShip = Math.max(0, allocated - pending);
                      const quantity = shipQuantities[line.id] || 0;
                      const lineLabelCount = labelCounts[line.id] || 0;
                      
                      // Only outstanding items - shipped lines are reversed via
                      // the Unship Order button, not from this table
                      if (availableToShip === 0 && !selectedLines[line.id]) return null;
                      
                      const savedWeight = parseFloat(line.product_weight) || 0;
                      const currentWeight = lineWeights[line.id] !== undefined ? lineWeights[line.id] : savedWeight;
                      const weightChanged = currentWeight !== savedWeight && currentWeight > 0;
                      const isSavingWeight = savingWeight[line.id];
                      
                      return (
                        <TableRow key={line.id} selected={selectedLines[line.id]}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLines[line.id] || false}
                              onChange={(e) => handleSelectLine(line.id, e.target.checked)}
                              disabled={availableToShip === 0 || processing}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {line.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {line.product_display}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={availableToShip} 
                              size="small" 
                              color={availableToShip > 0 ? "success" : "default"}
                            />
                            {pending > 0 && (
                              <Typography variant="caption" color="warning.main" display="block">
                                ({pending} pending)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={shipped}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={quantity}
                              onChange={(e) => handleQuantityChange(line.id, e.target.value, availableToShip)}
                              disabled={!selectedLines[line.id] || processing}
                              inputProps={{ 
                                min: 0,
                                max: availableToShip,
                                style: { textAlign: 'center' }
                              }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={currentWeight || ''}
                                onChange={(e) => handleWeightChange(line.id, e.target.value)}
                                disabled={processing}
                                placeholder="1"
                                inputProps={{
                                  min: 0,
                                  step: 0.1,
                                  style: { textAlign: 'center' }
                                }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                                }}
                                sx={{
                                  width: 110,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: savedWeight > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)'
                                  }
                                }}
                              />
                              {weightChanged && (
                                <Tooltip title="Save weight to product">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleSaveWeight(line)}
                                    disabled={isSavingWeight || processing}
                                  >
                                    {isSavingWeight ? <CircularProgress size={16} /> : <Save />}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {savedWeight > 0 && !weightChanged && (
                                <Tooltip title="Weight saved">
                                  <Scale fontSize="small" color="success" />
                                </Tooltip>
                              )}
                              {savedWeight === 0 && !weightChanged && (
                                <Tooltip title="No weight set - defaults to 1 kg">
                                  <Warning fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={lineLabelCount}
                                onChange={(e) => handleLabelCountChange(line.id, e.target.value)}
                                disabled={!selectedLines[line.id] || quantity === 0 || processing}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'center' }
                                }}
                                sx={{
                                  width: 70,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: lineLabelCount === 0 && quantity > 0 ? 'rgba(255, 152, 0, 0.2)' : 'inherit'
                                  }
                                }}
                              />
                              {lineLabelCount === 0 && quantity > 0 && (
                                <Tooltip title="No label - item will ship without its own label">
                                  <LabelOff fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                              {isHeavyWeight(getEffectiveWeight(line)) && (
                                <Tooltip title={`Item is ${heavyGoodsSettings.weight_threshold} kg or over — print heavy goods label`}>
                                  <span>
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      startIcon={<Print />}
                                      onClick={handlePrintHeavyGoodsLabel}
                                      disabled={printingHeavyLabel || processing}
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      Heavy
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {getStockItems().map(sc => {
                      const state = getStockItemState(sc.id);
                      const isSelectable = state === 'available';
                      const labelCount = stockItemLabelCounts[sc.id] !== undefined ? stockItemLabelCounts[sc.id] : 1;
                      const weight = stockItemWeights[sc.id] !== undefined ? stockItemWeights[sc.id] : '';

                      return (
                        <TableRow key={`stockitem-${sc.id}`} selected={!!selectedStockItems[sc.id]}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={(selectedStockItems[sc.id] && isSelectable) || false}
                              onChange={(e) => setSelectedStockItems(prev => ({ ...prev, [sc.id]: e.target.checked }))}
                              disabled={!isSelectable || processing}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label="STOCKITEM" size="small" color="secondary" variant="outlined" />
                              <Typography variant="body2" fontWeight="bold">
                                {sc.value}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={isSelectable ? 1 : 0}
                              size="small"
                              color={isSelectable ? 'success' : 'default'}
                            />
                            {state === 'pending' && (
                              <Typography variant="caption" color="warning.main" display="block">
                                (pending)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={state === 'shipped' ? 1 : 0}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">1</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={weight}
                              onChange={(e) => setStockItemWeights(prev => ({ ...prev, [sc.id]: parseFloat(e.target.value) || 0 }))}
                              disabled={!isSelectable || processing}
                              placeholder="1"
                              inputProps={{ min: 0, step: 0.1, style: { textAlign: 'center' } }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                              }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={labelCount}
                                onChange={(e) => setStockItemLabelCounts(prev => ({ ...prev, [sc.id]: Math.max(0, parseInt(e.target.value) || 0) }))}
                                disabled={!selectedStockItems[sc.id] || !isSelectable || processing}
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                sx={{ width: 70 }}
                              />
                              {isHeavyWeight(weight > 0 ? weight : 1) && (
                                <Tooltip title={`Item is ${heavyGoodsSettings.weight_threshold} kg or over — print heavy goods label`}>
                                  <span>
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      startIcon={<Print />}
                                      onClick={handlePrintHeavyGoodsLabel}
                                      disabled={printingHeavyLabel || processing}
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      Heavy
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Delivery Service - Read Only Display */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Delivery Service</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={order.delivery_service || 'ON'}
                  color={getDeliveryServiceColor(order.delivery_service)}
                  size="large"
                  sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, px: 1 }}
                />
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {getDeliveryServiceLabel(order.delivery_service)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Set from Dispatch Management
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Shipment Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Shipment Summary</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Lines Selected" 
                    secondary={selectedLinesCount}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Total Items to Ship" 
                    secondary={totalItemsToShip}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Labels to Create" 
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {totalLabelsToCreate}
                        {totalLabelsToCreate > 0 && (
                          <Chip label="1 consignment" size="small" color="success" />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Total Consignment Weight"
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Scale fontSize="small" />
                        {totalShipmentWeight > 0 ? `${Math.round(totalShipmentWeight * 100) / 100} kg` : '0 kg'}
                        {totalLabelsToCreate > 0 && totalShipmentWeight > 0 && (
                          <Chip
                            label={`~${Math.round((totalShipmentWeight / totalLabelsToCreate) * 10) / 10} kg/label`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {linesUsingDefaultWeight > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Warning fontSize="small" color="warning" />
                          Default Weight Used
                        </Box>
                      }
                      secondary={`${linesUsingDefaultWeight} line${linesUsingDefaultWeight !== 1 ? 's' : ''} using default 1 kg (no weight set)`}
                    />
                  </ListItem>
                )}
                {itemsWithoutLabels > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LabelOff fontSize="small" color="warning" />
                          Items Without Labels
                        </Box>
                      }
                      secondary={`${itemsWithoutLabels} items will ship without individual labels`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Actions</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={processing ? <CircularProgress size={20} /> : <Label />}
                  onClick={handleCreateConsignment}
                  disabled={processing || selectedLinesCount === 0 || totalItemsToShip === 0 || totalLabelsToCreate === 0}
                  fullWidth
                >
                  Create Consignment ({totalLabelsToCreate} label{totalLabelsToCreate !== 1 ? 's' : ''})
                </Button>
                
                <Divider>
                  <Typography variant="caption" color="text.secondary">OR</Typography>
                </Divider>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={processing ? <CircularProgress size={20} /> : <CheckCircle />}
                  onClick={handleShipWithoutDX}
                  disabled={processing || selectedLinesCount === 0 || totalItemsToShip === 0}
                  fullWidth
                >
                  Ship Without DX ({totalItemsToShip} items)
                </Button>
                
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  "Ship Without DX" marks items as shipped without creating any DX labels or tracking
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unship Order Dialog */}
      <Dialog open={unshipDialogOpen} onClose={() => !unshipping && setUnshipDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unship Entire Order?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This reverses everything shipped on this order: items return to stock,
            get re-allocated to their lines, and the order status rolls back so it
            can be shipped again. ALL of this order's consignments are cancelled
            at DX and deleted — you will need to create new ones.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The following will be unshipped:
          </Typography>
          <List dense>
            {order.lines?.filter(l => (parseFloat(l.quantity_shipped) || 0) > 0).map(l => (
              <ListItem key={l.id}>
                <ListItemText
                  primary={l.product_display || l.product_name}
                  secondary={`Qty: ${parseFloat(l.quantity_shipped) || 0}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshipDialogOpen(false)} disabled={unshipping}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Undo />}
            onClick={handleUnshipOrder}
            disabled={unshipping}
          >
            {unshipping ? 'Unshipping...' : 'Unship Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Label Preview Dialog */}
      <Dialog
        open={labelPreviewDialog}
        onClose={() => setLabelPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Shipping Labels - {previewConsignmentNumber}
        </DialogTitle>
        <DialogContent>
          {previewLabelData && (
            <Box sx={{ mt: 2 }}>
              <iframe
                src={`data:application/pdf;base64,${previewLabelData}`}
                width="100%"
                height="600px"
                title="Shipping Label Preview"
                style={{ border: 'none' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelPreviewDialog(false)}>
            Close
          </Button>
          <Button 
            startIcon={<Print />}
            onClick={handlePrintFromPreview}
            variant="outlined"
            color="primary"
          >
            Print
          </Button>
          <Button 
            startIcon={<Download />}
            onClick={handleDownloadFromPreview}
            variant="contained"
            color="primary"
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmDialog}
        onClose={() => {
          setDeleteConfirmDialog(false);
          setConsignmentToDelete(null);
        }}
        onConfirm={confirmDeleteConsignment}
        consignment={consignmentToDelete}
        processing={processing}
      />

      {/* Tracking Dialog */}
      <TrackingDialog
        open={trackingDialog}
        onClose={() => {
          setTrackingDialog(false);
          setTrackingConsignment(null);
        }}
        consignment={trackingConsignment}
        sessionKey={sessionKey}
      />
    </Box>
  );
}